<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    @save="onSave"
  >
    <template v-for="input in inputs" #[`${input.name}.input`]="data">
      <v-col :key="input.name" cols="3">
        <data-input v-model="settings[input.name]" v-bind="data" @change="setSettingsChanged" />
      </v-col>
    </template>
  </admin-wrapper>
</template>
<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

export default {
  name: 'IlsSettings',
  components: { AdminWrapper, DataInput },
  computed: {
    additionalModuleName: get('admin/additionalModules@ILS_MODULE.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@ils_module_enabled'),
    dataSources: get('layers/metadata'),
    ilsSettings: get('admin/modulesMapping@ils'),
    moduleEnabler() {
      return {
        name: 'ils_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    noSpatialDataSources() {
      return Object.values(this.dataSources).filter(dt => dt.scope === 'core' && !dt.geometry_type);
    },
    pointDataSources() {
      return Object.values(this.dataSources).filter(
        dt => dt.scope === 'core' && ['point', 'multipoint'].includes(dt.geometry_type)
      );
    },
    inputs() {
      const defaultProps = {
        dataType: {
          name: 'select',
        },
        rules: [v => !!v || v == '0' || 'rules.required'],
        align: 'start',
        itemText: 'verbose_name',
        itemValue: 'name',
      };
      return [
        {
          name: 'ils_street_lamp_datasource',
          translationPath: 'admin.ils.lanternsDataSource',
          items: this.pointDataSources,
          ...defaultProps,
        },
        {
          name: 'ils_pick_arm_datasource',
          translationPath: 'admin.ils.pickarmDataSource',
          items: this.noSpatialDataSources,
          ...defaultProps,
        },
        {
          name: 'ils_head_datasource',
          translationPath: 'admin.ils.headDataSource',
          items: this.noSpatialDataSources,
          ...defaultProps,
        },
      ];
    },
  },
  data: () => ({
    isFormFetched: false,
    settings: {},
    settingsChanged: false,
  }),
  methods: {
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getDataSources: call('layers/getLayersMetadata'),
    getModulesMapping: call('admin/getModulesMapping'),
    setSettingsChanged() {
      this.settingsChanged = true;
    },
    pushSuccessfullMessage() {
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message: this.$i18n.t('success.successEditSetting'),
      });
    },
    async onSave() {
      if (!this.settingsChanged) {
        this.pushSuccessfullMessage();
        return;
      }
      try {
        for await (let [name, value] of Object.entries(this.settings)) {
          if (value !== this.currentlySavedSettings[name]) {
            const payload = { name: name, settings: { datasource: value } };
            this.editDatasourceSettings(payload);
          }
        }
        this.currentlySavedSettings = JSON.parse(JSON.stringify(this.settings));
        this.settingsChanged = false;
        this.pushSuccessfullMessage();
      } catch {
        //
      }
    },
    async init() {
      this.isFormFetched = false;
      try {
        await Promise.all([this.getModulesMapping(), this.getDataSources({ with_attributes_schema: true })]);
        if (this.isModuleEnabled) {
          this.settings = JSON.parse(JSON.stringify(this.ilsSettings));
          /**
           * Creating non-reactive variable that detects individual changes in datasources
           * to prevent sending more requests than needed
           */
          this.currentlySavedSettings = JSON.parse(JSON.stringify(this.ilsSettings));
        }
        this.isFormFetched = true;
      } catch {
        //
      }
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
